<script setup>
import { inject } from "vue";
import { useI18n } from "vue-i18n";
import SvgIcon from "@/shared/ui/svg-icon/SvgIcon.vue";
import { UserAvatar } from "@/shared/ui/user-avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/ui/tooltip";
import UserCardCover from "./UserCardCover.vue";
import UserCardIdentity from "./UserCardIdentity.vue";
import UserCardLocation from "./UserCardLocation.vue";
import UserCardOrganization from "./UserCardOrganization.vue";
import UserCardIdentifier from "./UserCardIdentifier.vue";
import UserCardBio from "./UserCardBio.vue";
import UserCardContactList from "./UserCardContactList.vue";
import UserCardFooter from "./UserCardFooter.vue";
import { USER_PROFILE_DRAWER_KEY } from "@/widgets/user-profile-drawer";

const props = defineProps({
  user: { type: Object, required: true },
  editable: { type: Boolean, default: false },
});

const emit = defineEmits(["edit", "dismiss"]);
const { t } = useI18n();

const userProfileDrawer = inject(USER_PROFILE_DRAWER_KEY, null);

function onOpenProfile(e) {
  e?.stopPropagation?.();
  userProfileDrawer?.open(props.user.id);
  emit("dismiss");
}
</script>

<template>
  <article
    class="w-[360px] overflow-hidden rounded-[var(--radius)] bg-background text-foreground"
    role="group"
    :aria-label="
      [user.first_name, user.last_name].filter(Boolean).join(' ') ||
      t('Пользователь')
    "
  >
    <div class="relative">
      <UserCardCover :src="user.cover_url" @click="onOpenProfile" />
      <TooltipProvider :delay-duration="200">
        <div class="absolute right-3 top-3 flex items-center gap-2">
          <Tooltip>
            <TooltipTrigger as-child>
              <button
                type="button"
                :aria-label="t('Открыть профиль')"
                class="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/85 text-foreground backdrop-blur-sm transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                @click="onOpenProfile"
              >
                <SvgIcon
                  name="sli-arrow-right-up"
                  class="h-4 w-4"
                  aria-hidden="true"
                  style="fill: none"
                />
              </button>
            </TooltipTrigger>
            <TooltipContent>{{ t("Открыть профиль") }}</TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
    </div>

    <div class="relative px-5 pb-5">
      <div class="absolute -top-11 left-5">
        <UserAvatar
          :identifier="user.identifier"
          :seed="user.id"
          :name="[user.first_name, user.last_name].filter(Boolean).join(' ')"
          :src="user.avatar_url"
          :size="72"
          class="rounded-full ring-4 ring-background"
        />
      </div>

      <div class="pt-14 flex flex-col gap-3">
        <UserCardIdentity
          :first-name="user.first_name"
          :last-name="user.last_name"
          :position="user.position"
        />
        <UserCardLocation :location="user.location" />

        <UserCardIdentifier :identifier="user.identifier" />
        <UserCardBio :bio="user.bio" />
        <UserCardContactList
          :email="user.email"
          :phone="user.phone"
          :date-of-birth="user.date_of_birth"
        />
        <UserCardFooter :joined-at="user.joined_at" :socials="user.socials" />
        <UserCardOrganization
          :organization-id="user.organization_id"
          :organization-name="user.organization_name"
        />
      </div>
    </div>
  </article>
</template>
